import {useCallback, useEffect, useRef, useState} from 'react';
import {Swiper, SwiperSlide} from 'swiper/react';
import {Swiper as SwiperClass} from 'swiper/types';
import {t} from '../../../common/getTranslations';
import {preventDefaultEvent} from '../../../common/shared/utils/preventDefaultEvent.function';
import {twMerge} from '../../../common/twMerge';
import {useIsDarkMode} from '../../../common/useIsDarkMode.hook';
import {Fullscreen} from '../../atoms/Fullscreen/Fullscreen';
import {Icon} from '../../atoms/Icon/Icon';
import {IconType} from '../../atoms/Icon/IconType.enum';
import {Text} from '../../atoms/Text/Text';
import {ImagePopupProps} from './ImagePopupProps.interface';

export function ImagePopup({
  isMobile,
  imageList,
  focusIn,
  className,
  imageClassName,
  isPoster = true,
  ...props
}: ImagePopupProps) {
  const swiperRef = useRef<SwiperClass>();
  const showPage = isMobile && imageList.length > 1;
  const [currentIndex, setCurrentIndex] = useState(0);
  const isDarkMode = useIsDarkMode();

  useEffect(() => {
    if (focusIn !== undefined) {
      swiperRef.current?.slideTo(focusIn);
    }
  }, [focusIn]);
  const onKeyDown = useCallback((event: KeyboardEvent) => {
    event.preventDefault();
  }, []);
  useEffect(() => {
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onKeyDown]);
  return (
    <Fullscreen
      className={twMerge(
        'z-100 flex flex-col bg-static-etc-dimmed',
        showPage
          ? isDarkMode
            ? 'bg-static-mono-black'
            : 'bg-static-mono-white'
          : '',
        className
      )}
      {...props}
    >
      {isMobile && (
        <div
          className={twMerge(
            'flex h-56pxr w-full items-center px-15pxr py-18pxr',
            showPage ? 'justify-between' : 'justify-end'
          )}
        >
          {showPage && (
            <Text className="font-medium2-bold text-theme-solid-100">
              {currentIndex + 1}/{imageList.length}
            </Text>
          )}
          <Text
            className={twMerge(
              'font-small1 cursor-pointer',
              showPage ? 'text-theme-solid-60' : 'text-static-mono-10'
            )}
          >
            {t('common_close_button')}
          </Text>
        </div>
      )}
      {isMobile && (
        <Swiper
          className={'h-full w-full touch-pan-x select-none'}
          onSwiper={swiper => {
            if (focusIn !== undefined) {
              swiper.slideTo(focusIn, 0);
            }
          }}
          onSlideChange={swiper => {
            setCurrentIndex(swiper.realIndex);
          }}
        >
          {imageList.map((image, index) => {
            return (
              <SwiperSlide
                key={index}
                className="flex items-center justify-center"
              >
                <img
                  src={parseImageUrlToOriginalResolution(image)}
                  alt="이미지 크게보기"
                  className={twMerge(
                    'max-w-full mx-auto',
                    isPoster && 'max-h-full px-15pxr pb-15pxr',
                    imageClassName
                  )}
                />
              </SwiperSlide>
            );
          })}
        </Swiper>
      )}
      {!isMobile && (
        <div className="flex-center-full">
          <div className="relative">
            <img
              className="max-h-[calc(100vh-320px)] min-w-192pxr"
              src={parseImageUrlToOriginalResolution(imageList[0])}
              alt="이미지 크게보기"
              onSelect={preventDefaultEvent}
              onDragStart={preventDefaultEvent}
              onContextMenu={preventDefaultEvent}
            />
            <Icon
              type={IconType.ButtonCloseSolidMPcStatic}
              className="absolute top-16pxr right-10pxr z-110 h-24pxr w-24pxr"
            />
          </div>
        </div>
      )}
    </Fullscreen>
  );
}

// kage에서 원본이미지를 가져와 사용합니다. kage 주소 사용방식은 변할 일이 없을 것 같아 컴포넌트에서 직접 바꾸어 사용하도록 수정했습니다.
function parseImageUrlToOriginalResolution(src: string) {
  return src.replace(/&filename=th[0-9]+/, '&filename=o1');
}
